import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import styles from './WebsitePage.module.css';
import { useLang } from '../../context/LanguageContext';

/* ── Icons ────────────────────────────────────────────── */
const IconVitrine = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>
  </svg>
);
const IconLanding = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
  </svg>
);
const IconCorporate = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M9 21V7l3-4 3 4v14M3 10h4v11H3z M17 10h4v11h-4z"/>
  </svg>
);
const IconRefonte = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);
const IconResponsive = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
);
const IconUX = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);
const IconCRM = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.87 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.77 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IconSEO = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
  </svg>
);

const deliverables = [
  { icon: <IconVitrine />,   label: { fr: 'Site vitrine',                       en: 'Showcase site'              }, desc: { fr: 'Présentation claire de votre activité, services et contacts.',              en: 'Clear presentation of your activities, services and contact info.'          } },
  { icon: <IconLanding />,   label: { fr: 'Landing page',                       en: 'Landing page'               }, desc: { fr: 'Page unique optimisée pour convertir un visiteur en prospect.',          en: 'Single page optimized to turn a visitor into a prospect.'                   } },
  { icon: <IconCorporate />, label: { fr: 'Site corporatif',                    en: 'Corporate site'             }, desc: { fr: 'Présence en ligne complète pour une entreprise établie.',               en: 'Full online presence for an established company.'                           } },
  { icon: <IconRefonte />,   label: { fr: 'Refonte de site existant',           en: 'Site redesign'              }, desc: { fr: 'Moderniser un site vieillissant en conservant votre contenu.',          en: 'Modernize an aging site while preserving your content.'                     } },
  { icon: <IconResponsive />,label: { fr: 'Optimisation responsive',            en: 'Responsive optimization'   }, desc: { fr: 'Mobile, tablette et desktop — chaque taille parfaitement gérée.',      en: 'Mobile, tablet and desktop — every size perfectly handled.'                 } },
  { icon: <IconUX />,        label: { fr: 'Amélioration design & UX',           en: 'Design & UX improvement'   }, desc: { fr: 'Refonte visuelle centrée sur la clarté et la confiance.',               en: 'Visual redesign focused on clarity and trust.'                              } },
  { icon: <IconCRM />,       label: { fr: 'Formulaire & intégration CRM',       en: 'Form & CRM integration'    }, desc: { fr: 'Capturer les leads et les connecter à vos outils existants.',           en: 'Capture leads and connect them to your existing tools.'                     } },
  { icon: <IconSEO />,       label: { fr: 'SEO de base',                        en: 'Basic SEO'                  }, desc: { fr: 'Structure, balises, performance — les fondations indispensables.',     en: 'Structure, tags, performance — the essential foundations.'                 } },
];

const clients = [
  {
    num: '01',
    title: { fr: 'La PME sans site professionnel',      en: 'The business with no professional site' },
    desc:  { fr: "Vous avez une activité sérieuse mais votre présence en ligne ne le reflète pas — ou elle n'existe pas encore.",           en: "You run a serious business but your online presence doesn't reflect it — or doesn't exist yet." },
  },
  {
    num: '02',
    title: { fr: "L'entreprise avec un site vieillissant",  en: 'The company with an outdated site' },
    desc:  { fr: "Votre site a plus de cinq ans, il n'est pas mobile-friendly et ne reflète plus votre positionnement actuel.", en: "Your site is over five years old, not mobile-friendly and no longer reflects your current positioning." },
  },
  {
    num: '03',
    title: { fr: "L'entrepreneur qui lance une offre",      en: 'The entrepreneur launching an offer' },
    desc:  { fr: "Vous avez besoin d'une landing page rapide, percutante, pour tester ou lancer votre nouvelle offre.",       en: "You need a fast, punchy landing page to test or launch your new offer." },
  },
];

const differentiators = [
  {
    num: '→',
    title: { fr: 'Un seul interlocuteur',    en: 'One point of contact'     },
    desc:  { fr: "Du brief au déploiement, je suis là. Pas de chef de projet intermédiaire, pas de perte d'information.", en: "From brief to deployment, I'm there. No middleman PM, no information loss." },
  },
  {
    num: '→',
    title: { fr: 'Livraison rapide',          en: 'Fast delivery'            },
    desc:  { fr: "Sans processus d'agence alourdi. Un site vitrine en 2–3 semaines, une landing page en quelques jours.",    en: "No bloated agency process. A showcase site in 2–3 weeks, a landing page in a few days." },
  },
  {
    num: '→',
    title: { fr: 'Design adapté à votre secteur', en: 'Design tailored to your sector' },
    desc:  { fr: 'Pas un template. Chaque choix visuel est pensé pour votre secteur, votre audience et votre positionnement.', en: 'Not a template. Every visual choice is designed for your sector, audience and positioning.' },
  },
];

const techs = ['Next.js', 'React', 'Tailwind CSS', 'Figma', 'Vercel', 'WordPress'];

/* ── Animation ────────────────────────────────────────── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const fadeUp  = { hidden: { opacity: 0, y: 28, filter: 'blur(4px)' }, visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

export default function WebsitePage() {
  const delivRef  = useRef(null);
  const clientRef = useRef(null);
  const diffRef   = useRef(null);
  const delivInView  = useInView(delivRef,  { once: true, margin: '-60px' });
  const clientInView = useInView(clientRef, { once: true, margin: '-60px' });
  const diffInView   = useInView(diffRef,   { once: true, margin: '-60px' });
  const { lang } = useLang();

  const ui = {
    fr: {
      breadcrumb:  'Sites web pour entreprises',
      heroTitle:   'Des sites web professionnels qui donnent',
      heroAccent:  "confiance au premier coup d\u2019\u0153il.",
      heroSub:     'Je cr\u00e9e des sites web modernes, cr\u00e9dibles et orient\u00e9s conversion pour aider les entreprises \u00e0 pr\u00e9senter clairement leurs services.',
      ctaPrimary:  'D\u00e9marrer un projet web',
      ctaGhost:    'Voir les livrables',
      delivEy:     'Ce que je livre',
      delivT1:     'Chaque format,',
      delivT2:     'ma\u00eetris\u00e9 de bout en bout.',
      clientsEy:   'Pour qui\u00a0?',
      clientsT1:   'Trois situations',
      clientsT2:   'que je connais bien.',
      diffEy:      'Ce qui me diff\u00e9rencie',
      diffT1:      'Pas une agence.',
      diffT2:      'Un d\u00e9veloppeur engag\u00e9.',
      techLabel:   'Technologies utilis\u00e9es',
    },
    en: {
      breadcrumb:  'Business websites',
      heroTitle:   'Professional websites that build',
      heroAccent:  'trust at first glance.',
      heroSub:     'I build modern, credible, conversion-focused websites to help businesses present their services clearly.',
      ctaPrimary:  'Start a web project',
      ctaGhost:    'See deliverables',
      delivEy:     'What I deliver',
      delivT1:     'Every format,',
      delivT2:     'mastered end to end.',
      clientsEy:   'Who is this for?',
      clientsT1:   'Three situations',
      clientsT2:   'I know well.',
      diffEy:      'What sets me apart',
      diffT1:      'Not an agency.',
      diffT2:      'A committed developer.',
      techLabel:   'Technologies used',
    },
  }[lang];

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.div className={styles.breadcrumb}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Link to="/services" className={styles.breadcrumbLink}>Services</Link>
            <span className={styles.sep}>/</span>
            <span>{ui.breadcrumb}</span>
          </motion.div>

          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}>
            {ui.heroTitle}{' '}
            <span className={styles.accent}>{ui.heroAccent}</span>
          </motion.h1>

          <motion.p className={styles.heroSub}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.24 }}>
            {ui.heroSub}
          </motion.p>

          <motion.div className={styles.heroCtas}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut', delay: 0.36 }}>
            <Link to="/contact" className={styles.ctaPrimary}>
              {ui.ctaPrimary}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href="#livrables" className={styles.ctaGhost}>{ui.ctaGhost}</a>
          </motion.div>
        </div>
      </section>

      {/* ── LIVRABLES ── */}
      <section id="livrables" className={styles.section} ref={delivRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.delivEy}</p>
            <h2 className={styles.sectionTitle}>{ui.delivT1}<br />{ui.delivT2}</h2>
          </div>
          <motion.div className={styles.delivGrid} variants={stagger} initial="hidden" animate={delivInView ? 'visible' : 'hidden'}>
            {deliverables.map((d) => (
              <motion.div key={d.label.fr} className={styles.delivCard} variants={fadeUp}>
                <div className={styles.delivIcon}>{d.icon}</div>
                <div>
                  <p className={styles.delivLabel}>{d.label[lang]}</p>
                  <p className={styles.delivDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── POUR QUI ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} ref={clientRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.clientsEy}</p>
            <h2 className={styles.sectionTitle}>{ui.clientsT1}<br />{ui.clientsT2}</h2>
          </div>
          <motion.div className={styles.clientGrid} variants={stagger} initial="hidden" animate={clientInView ? 'visible' : 'hidden'}>
            {clients.map((c) => (
              <motion.div key={c.num} className={styles.clientCard} variants={fadeUp}>
                <span className={styles.clientNum}>{c.num}</span>
                <h3 className={styles.clientTitle}>{c.title[lang]}</h3>
                <p className={styles.clientDesc}>{c.desc[lang]}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── DIFFÉRENCIATEURS ── */}
      <section className={styles.section} ref={diffRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.diffEy}</p>
            <h2 className={styles.sectionTitle}>{ui.diffT1}<br />{ui.diffT2}</h2>
          </div>
          <motion.div className={styles.diffList} variants={stagger} initial="hidden" animate={diffInView ? 'visible' : 'hidden'}>
            {differentiators.map((d) => (
              <motion.div key={d.title.fr} className={styles.diffItem} variants={fadeUp}>
                <span className={styles.diffArrow}>{d.num}</span>
                <div>
                  <p className={styles.diffTitle}>{d.title[lang]}</p>
                  <p className={styles.diffDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TECHNOLOGIES ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} data-aos="fade-up">
        <div className={styles.container}>
          <div className={styles.techRow}>
            <p className={styles.techLabel}>{ui.techLabel}</p>
            <div className={styles.techBadges}>
              {techs.map((t) => (
                <span key={t} className={styles.techBadge}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}
